import { ContainerLeft, ContainerRight, ModuleContainer } from 'components/common/styles';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { ModalWrapper } from '../../../components/ModalWrapper';
import { IRefIds } from '../../../constants/refs';
import ModuleContext from '../../../CMFW/context/ModuleContext';
import { Split } from '../../../components/Split';
import Grid from '../../../CMFW/grid/Grid';
import { IGridData } from '../../../types/models';
import { useForceRender } from '../../../hooks/useForceRender';
import AudioArtistModel from '../../../models/AudioArtistModel';
import { IAudioArtist } from '../../../types/audioArtist';
import Chip from '@material-ui/core/Chip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { Button } from '@material-ui/core';
import AudioCreateSetModel, { 
  IAudioWordsCount,
  IAudioWordsCountSimple,
  IAudioWordsCountGender,
  IAudioWordsCountMaleFemale
} from '../../../models/AudioCreateSetModel';
import CircularProgress from '@material-ui/core/CircularProgress';
import AudioItemsButton from '../../../components/common/AudioItemsButton';

type IProps = {
  onClose: () => void;
  onReloadDashboard: () => void;
  courseId: number;
  refId: IRefIds;
  targetDialectId: number;
  name: string;
  startLesson: number;
  endLesson: number;
};

// Type guards for different word count formats
function isSimpleWordCount(data: IAudioWordsCount): data is IAudioWordsCountSimple {
  return 'any' in data.wordCounts;
}

function isGenderWordCount(data: IAudioWordsCount): data is IAudioWordsCountGender {
  return 'm' in data.wordCounts && 'f' in data.wordCounts && 'mf' in data.wordCounts;
}

function isMaleFemaleWordCount(data: IAudioWordsCount): data is IAudioWordsCountMaleFemale {
  return 'm' in data.wordCounts && 'f' in data.wordCounts && !('mf' in data.wordCounts);
}

const AudioCreateSets: React.FC<IProps> = (props) => {
  const forceRender = useForceRender();

  const audioArtistModel = useMemo(
    () =>
      new AudioArtistModel({ render: forceRender, courseId: props.courseId, targetDialectId: props.targetDialectId }),
    [forceRender, props.courseId, props.targetDialectId],
  );

  const audioCreateSetModel = useMemo(() => new AudioCreateSetModel(), []);

  const [audioArtists, setAudioArtists] = React.useState([] as IAudioArtist[]);
  const [wordsCount, setWordsCount] = React.useState(null as IAudioWordsCount | null);
  const [creatingSets, setCreatingSets] = React.useState(false);

  useEffect(() => {
    (async () => {
      const words = await audioCreateSetModel.getWordsCount(
        props.courseId,
        props.targetDialectId,
        props.refId,
        props.startLesson,
        props.endLesson,
      );
      setWordsCount(words);
    })();
  }, [audioCreateSetModel, props.courseId, props.targetDialectId, props.refId, props.startLesson, props.endLesson]);

  const onRowClick = (data: IGridData, selectedRows: IGridData[]) => {
    const row = selectedRows[0] as IAudioArtist | undefined;
    if (row && !creatingSets) {
      const found = audioArtists.find((artist) => artist.id === row.id);
      if (!found) {
        setAudioArtists([...audioArtists, row]);
      }
    }
  };

  const handleRemoveArtist = (audioArtist: IAudioArtist) => {
    if (!creatingSets) {
      setAudioArtists(audioArtists.filter((artist) => artist.id !== audioArtist.id));
    }
  };

  const handleCreateSets = async () => {
    setCreatingSets(true);
    await audioCreateSetModel.createSets(
      props.courseId,
      props.targetDialectId,
      props.refId,
      props.startLesson,
      props.endLesson,
      audioArtists.map((audioArtist) => audioArtist.id),
    );
    props.onClose();
    props.onReloadDashboard();
  };

  const renderGenderWordCounts = (keys: readonly string[], wordCounts: Record<string, number>, ids: Record<string, number[]>) => {
    return (
      <WordCountTable>
        <WordCountTableHeader>
          <tr>
            <th>Gender</th>
            <th>Words</th>
            <th></th>
          </tr>
        </WordCountTableHeader>
        <WordCountTableBody>
          {keys.map((key) => (
            <tr key={key}>
              <GenderCell>{key}</GenderCell>
              <CountCell>{wordCounts[key]}</CountCell>
              <ActionCell>
                {ids[key] && Array.isArray(ids[key]) && (
                  <AudioItemsButton
                    courseId={props.courseId}
                    refId={props.refId}
                    ids={ids[key]}
                    disabled={creatingSets}
                  />
                )}
              </ActionCell>
            </tr>
          ))}
        </WordCountTableBody>
      </WordCountTable>
    );
  };

  const renderWordCountsAndIds = () => {
    if (!wordsCount) {
      return <CircularProgress size={16} />;
    }

    if (isSimpleWordCount(wordsCount)) {
      return (
        <WordCountTable>
          <WordCountTableHeader>
            <tr>
              <th>Type</th>
              <th>Words</th>
              <th></th>
            </tr>
          </WordCountTableHeader>
          <WordCountTableBody>
            <tr>
              <GenderCell>All</GenderCell>
              <CountCell>{wordsCount.wordCounts.any}</CountCell>
              <ActionCell>
                <AudioItemsButton
                  courseId={props.courseId}
                  refId={props.refId}
                  ids={wordsCount.ids}
                  disabled={creatingSets}
                />
              </ActionCell>
            </tr>
          </WordCountTableBody>
        </WordCountTable>
      );
    }

    if (isGenderWordCount(wordsCount) || isMaleFemaleWordCount(wordsCount)) {
      const { wordCounts, ids } = wordsCount;
      const keys = isGenderWordCount(wordsCount) ? (['m', 'f', 'mf'] as const) : (['m', 'f'] as const);
      return renderGenderWordCounts(keys, wordCounts, ids);
    }

    return null;
  };

  return (
    <ModalWrapper onClose={props.onClose} title={`Create Sets for ${props.name}`} width={600} height={600}>
      <StyledModuleContainer>
        <ModuleContext.Provider value={audioArtistModel}>
          <Split widthLeft={300}>
            <ContainerLeft>
              <Grid handleRowClick={onRowClick} />
            </ContainerLeft>
            <StyledContainerRight>
              <WordCountContainer>
                {wordsCount ? (
                  renderWordCountsAndIds()
                ) : (
                  <LoadingContainer>
                    <CircularProgress size={16} />
                  </LoadingContainer>
                )}
              </WordCountContainer>
              <AudioArtistSection>
                <SectionTitle>Selected Audio Artists</SectionTitle>
                <AudioArtistChipContainer>
                  {audioArtists.length === 0 ? (
                    <EmptyMessage>No artists selected. Click on an artist from the left panel.</EmptyMessage>
                  ) : (
                    audioArtists.map((audioArtist) => (
                      <AudioArtistChip
                        key={audioArtist.id}
                        label={`${audioArtist.name} [ ${audioArtist.gender} ]`}
                        onDelete={() => handleRemoveArtist(audioArtist)}
                        deleteIcon={<FontAwesomeIcon icon={faTimesCircle} />}
                        clickable={false}
                      />
                    ))
                  )}
                </AudioArtistChipContainer>
              </AudioArtistSection>
              <ButtonContainer>
                {creatingSets ? (
                  <CreatingContainer>
                    <CircularProgress size={20} />
                    <span>Creating sets...</span>
                  </CreatingContainer>
                ) : (
                  <CreateButton
                    variant="contained"
                    onClick={handleCreateSets}
                    disabled={audioArtists.length === 0 || wordsCount === null}
                  >
                    CREATE SETS
                  </CreateButton>
                )}
              </ButtonContainer>
            </StyledContainerRight>
          </Split>
        </ModuleContext.Provider>
      </StyledModuleContainer>
    </ModalWrapper>
  );
};

const AudioArtistChip = styled(Chip)`
  && {
    margin: 4px;
    background-color: #e8f0fe;
    color: #1967d2;
    height: 32px;
    font-size: 13px;
    font-weight: 500;
    border: 1px solid #dadce0;
    
    .MuiChip-deleteIcon {
      color: #5f6368;
      font-size: 16px;
      
      &:hover {
        color: #d32f2f;
      }
    }
  }
`;

const AudioArtistChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  min-height: 200px;
  overflow-y: auto;
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  align-content: flex-start;
`;

const WordCountContainer = styled.div`
  margin-bottom: 16px;
`;

const WordCountTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const WordCountTableHeader = styled.thead`
  tr {
    background-color: #f5f6f7;
  }
  
  th {
    padding: 8px 12px;
    text-align: left;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #5f6368;
    border-bottom: 2px solid #e0e0e0;
    
    &:last-child {
      text-align: right;
    }
  }
`;

const WordCountTableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #f0f0f0;
    
    &:hover {
      background-color: #f8f9fa;
    }
  }
  
  td {
    padding: 10px 12px;
    vertical-align: middle;
  }
`;

const GenderCell = styled.td`
  font-weight: 600;
  text-transform: uppercase;
  color: #2d3748;
  font-size: 14px;
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 8px;
    background-color: ${props => {
      const gender = props.children?.toString().toLowerCase();
      if (gender === 'm') return '#3182ce';
      if (gender === 'f') return '#e53e3e';
      if (gender === 'mf') return '#9f7aea';
      return '#718096';
    }};
  }
`;

const CountCell = styled.td`
  font-weight: 600;
  color: #2d3748;
  font-size: 14px;
`;

const ActionCell = styled.td`
  text-align: right;
`;

const SectionTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #202124;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const AudioArtistSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const EmptyMessage = styled.p`
  font-size: 13px;
  color: #5f6368;
  text-align: center;
  margin: 24px 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const CreateButton = styled(Button)`
  && {
    background-color: #1a73e8;
    color: white;
    font-weight: 600;
    font-size: 14px;
    padding: 8px 24px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15);
    
    &:hover {
      background-color: #1765cc;
      box-shadow: 0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15);
    }
    
    &:disabled {
      background-color: #f1f3f4;
      color: #80868b;
      box-shadow: none;
    }
  }
`;

const CreatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #5f6368;
  font-size: 14px;
`;

const StyledContainerRight = styled(ContainerRight)`
  padding: 20px;
  border-left: 1px solid #e0e0e0;
  background-color: #fafafa;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledModuleContainer = styled(ModuleContainer)`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export default AudioCreateSets;
